import React from 'react'

import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { Menu as MenuIcon } from 'src/components/icon'
import { InformationMessage } from 'src/components/InformationMessage/InformationMessage'
import { MemberLabel } from 'src/components/MemberLabel/MemberLabel'
import { WarningMessage } from 'src/components/WarningMessage/WarningMessage'
import { useOpenModal } from 'src/hooks/use-open-modal'
import { useOpenPullDown } from 'src/hooks/use-open-pull-down'
import { useBreadcrumb } from 'src/hooks/useBreadcrumb'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import { formatDate } from 'src/utils/formatDate'

import { IcfDocumentCardReadOnlyRenewal } from '../components/document-card-readonly-renewal'
import { IcfDocumentCardRenewal } from '../components/document-card-renewal'
import { WithCommentModalRenewal } from '../components/with-comment-modal-renewal'
import {
  IcfDocumentRevision,
  IcfDocumentDetail,
  IcfDocumentType,
  IcfDocumentRevisionHistory,
  IcfDocumentSignerRole,
  IcfDocumentCheckRole,
  icfRevActionText,
} from '../entity'
import { IcfDocumentErrors } from '../validate'

type Props = {
  icfDocument: IcfDocumentDetail | undefined
  latestRevision: IcfDocumentRevision | undefined
  listPath: string
  shouldSelectHospital: boolean
  hospitalName: string
  latestFilePreviewUrl: string
  onRedirectDocuSignEdit: () => void
  onRedirectDocuSignPreview: (
    role: IcfDocumentSignerRole | IcfDocumentCheckRole,
  ) => void
  onChangeName: (value: string) => void
  onChangeVersion: (value: string) => void
  onChangeDocumentType: (type: IcfDocumentType) => void
  onChangeRequiredPatientSign: () => void
  onSelectFile: (file: File) => Promise<void>
  errors: IcfDocumentErrors
  canChangeDocumentType: boolean
  canUpdate: boolean
  canEdit: boolean
  canPublish: boolean
  canDisable: boolean
  canAddRevision: boolean
  canSetDocuSignRole: boolean
  hasSetDocuSignRole: boolean
  onValidate: () => boolean
  onSubmitUpdate: () => Promise<void>
  onSubmitDelete: () => Promise<void>
  onPublish: () => Promise<void>
  onRequestApproval: (comment: string) => Promise<void>
  onWithdraw: (comment: string) => Promise<void>
  onApprove: (comment: string) => Promise<void>
  onReject: (comment: string) => Promise<void>
  onDisable: (comment: string) => Promise<void>
  onEnable: (comment: string) => Promise<void>
  onCancelRevision: () => Promise<void>
  navigateToAddRevision: () => void
  pullDownState: ReturnType<typeof useOpenPullDown>
  deleteModalState: ReturnType<typeof useOpenModal>
  requestApprovalModalState: ReturnType<typeof useOpenModal>
  publishModalState: ReturnType<typeof useOpenModal>
  withdrawModalState: ReturnType<typeof useOpenModal>
  approveModalState: ReturnType<typeof useOpenModal>
  rejectModalState: ReturnType<typeof useOpenModal>
  disableModalState: ReturnType<typeof useOpenModal>
  enableModalState: ReturnType<typeof useOpenModal>
  hasMultipleRevisionHistories: boolean
  hasTempFile: boolean
  openRevisionHistoryPane: () => void
}

const operatedAtDesc = (
  a: IcfDocumentRevisionHistory,
  b: IcfDocumentRevisionHistory,
) => {
  const aTime = new Date(a.operatedAt).getTime()
  const bTime = new Date(b.operatedAt).getTime()

  return bTime - aTime
}

export const EditIcfDocumentRenewal: React.FC<Props> = ({
  icfDocument,
  latestRevision,
  errors,
  listPath,
  shouldSelectHospital,
  hospitalName,
  latestFilePreviewUrl,
  canChangeDocumentType,
  canUpdate,
  canEdit,
  canPublish,
  canDisable,
  canAddRevision,
  canSetDocuSignRole,
  hasSetDocuSignRole,
  deleteModalState,
  requestApprovalModalState,
  publishModalState,
  withdrawModalState,
  approveModalState,
  rejectModalState,
  disableModalState,
  enableModalState,
  hasMultipleRevisionHistories,
  hasTempFile,
  onRedirectDocuSignEdit,
  onRedirectDocuSignPreview,
  onChangeName,
  onChangeVersion,
  onChangeDocumentType,
  onChangeRequiredPatientSign,
  onSelectFile,
  onPublish,
  onWithdraw,
  onApprove,
  onReject,
  onCancelRevision,
  onValidate,
  onSubmitUpdate,
  onSubmitDelete,
  onRequestApproval,
  onDisable,
  onEnable,
  navigateToAddRevision,
  openRevisionHistoryPane,
}) => {
  const { renderBreadcrumb } = useBreadcrumb()

  if (!icfDocument || !latestRevision) {
    return null
  }

  return (
    <Flex direction="column" h="full" py="6">
      <Modal
        isOpen={deleteModalState.modalOpen}
        onClose={deleteModalState.handlers.closeModal}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>文書を削除</ModalHeader>
          <ModalBody>文書を削除します。よろしいですか？</ModalBody>
          <ModalFooter>
            <ModalCancelButton />
            <Button type="submit" onClick={onSubmitDelete} colorScheme="red">
              削除
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={publishModalState.modalOpen}
        onClose={publishModalState.handlers.closeModal}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>文書を公開しますか？</ModalHeader>
          <ModalBody>
            <Stack>
              <Text>
                公開文書の内容に間違いないことをご確認の上、公開するボタンで登録してください。
              </Text>
              <InformationMessage message="※公開後は文書セットに紐付けて下さい。" />
            </Stack>
          </ModalBody>
          <ModalFooter>
            <ModalCancelButton />
            <Button type="submit" onClick={onPublish}>
              公開する
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <WithCommentModalRenewal
        title="承認依頼"
        isOpen={requestApprovalModalState.modalOpen}
        onClose={requestApprovalModalState.handlers.closeModal}
        onSubmit={onRequestApproval}
        required={false}
        submitText="承認を依頼する"
        descriptionComponent={null}
      />
      <WithCommentModalRenewal
        title="承認依頼取り下げ"
        isOpen={withdrawModalState.modalOpen}
        onClose={withdrawModalState.handlers.closeModal}
        onSubmit={onWithdraw}
        required={false}
        submitText="承認依頼取り下げ"
        descriptionComponent={null}
      />
      <WithCommentModalRenewal
        title="承認"
        isOpen={approveModalState.modalOpen}
        onClose={approveModalState.handlers.closeModal}
        onSubmit={onApprove}
        required={false}
        submitText="承認"
        descriptionComponent={
          <div>
            <Text>
              <strong>{`文書ID:${icfDocument.numberingId} ${latestRevision.name}`}</strong>
              を承認します。
            </Text>
            <Text fontSize="sm" color="red.500">
              ※承認後は文書セットに紐付けて下さい。
            </Text>
          </div>
        }
      />
      <WithCommentModalRenewal
        title="承認却下"
        isOpen={rejectModalState.modalOpen}
        onClose={rejectModalState.handlers.closeModal}
        onSubmit={onReject}
        required={false}
        submitText="承認を却下する"
        descriptionComponent={null}
        buttonColorScheme="red"
      />

      <WithCommentModalRenewal
        title="文書を無効化"
        isOpen={disableModalState.modalOpen}
        onClose={disableModalState.handlers.closeModal}
        onSubmit={onDisable}
        required={false}
        submitText="無効化する"
        buttonColorScheme="red"
        descriptionComponent={
          <>
            <Text>
              <strong>{`文書ID:${icfDocument.numberingId} ${latestRevision.name}`}</strong>
              を無効化します。
            </Text>
            <WarningMessage message="説明開始済みの同意説明には反映されません。" />
          </>
        }
      />
      <WithCommentModalRenewal
        title="文書を有効化"
        isOpen={enableModalState.modalOpen}
        onClose={enableModalState.handlers.closeModal}
        onSubmit={onEnable}
        required={false}
        submitText="有効化する"
        descriptionComponent={
          <Text>
            <strong>{`文書ID:${icfDocument.numberingId} ${latestRevision.name}`}</strong>
            を有効化します。
          </Text>
        }
      />

      <Stack spacing="8" px="6">
        <Flex justify="space-between" wrap="wrap" rowGap={2}>
          {renderBreadcrumb([
            { text: '文書一覧', link: listPath },
            { text: '文書編集', isCurrentPage: true },
          ])}
          <Flex flex="1" alignItems="center" gap="4" justify="end">
            {canEdit && (
              <Button
                variant="outline"
                onClick={onSubmitUpdate}
                isDisabled={!canUpdate}
              >
                一時保存
              </Button>
            )}
            {canPublish && (
              <Button
                isDisabled={canUpdate}
                onClick={() => {
                  if (!onValidate()) {
                    return
                  }
                  publishModalState.handlers.openModal()
                }}
              >
                公開
              </Button>
            )}

            {latestRevision.latestStatus === 'Disabled' && (
              <Button onClick={enableModalState.handlers.openModal}>
                有効化
              </Button>
            )}
            {canAddRevision && (
              <Button onClick={navigateToAddRevision}>改版する</Button>
            )}
            {(icfDocument.canDelete ||
              latestRevision.canCancel ||
              canDisable) && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  variant="customIconButtonGhost"
                  icon={<MenuIcon />}
                />
                <MenuList>
                  {icfDocument.canDelete && (
                    <MenuItem
                      onClick={deleteModalState.handlers.openModal}
                      color="red.500"
                    >
                      文書を削除
                    </MenuItem>
                  )}
                  {latestRevision.canCancel && (
                    <MenuItem onClick={onCancelRevision} color="red.500">
                      改版取り消し
                    </MenuItem>
                  )}
                  {canDisable && (
                    <MenuItem
                      onClick={disableModalState.handlers.openModal}
                      color="red.500"
                    >
                      文書を無効化
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
          </Flex>
        </Flex>

        {shouldSelectHospital && (
          <Box>
            <Text fontSize="sm" fontWeight="bold">
              医療機関 : {hospitalName}
            </Text>
          </Box>
        )}
      </Stack>

      <Box flex="1" overflow="auto" mt="8" pt="4" px="6">
        <Box w="full" minW="640px" maxW="1024px">
          {canEdit ? (
            <IcfDocumentCardRenewal
              cardType="edit"
              status={latestRevision.latestStatus}
              numberingId={icfDocument.numberingId}
              canChangeDocumentType={canChangeDocumentType}
              canSetDocuSignRole={canSetDocuSignRole}
              version={latestRevision.version}
              name={latestRevision.name}
              documentType={latestRevision.type}
              fileName={latestRevision.fileName}
              isRequiredPatientSign={latestRevision.isRequiredPatientSign}
              filePreviewUrl={latestFilePreviewUrl}
              signFields={latestRevision.signFields}
              errors={errors}
              hasMultipleRevisionHistories={hasMultipleRevisionHistories}
              hasTempFile={hasTempFile}
              hasSetDocuSignRole={hasSetDocuSignRole}
              onChangeName={onChangeName}
              onChangeVersion={onChangeVersion}
              onChangeDocumentType={onChangeDocumentType}
              onChangeRequiredPatientSign={onChangeRequiredPatientSign}
              onSelectFile={onSelectFile}
              onRedirectDocuSignEdit={onRedirectDocuSignEdit}
              openRevisionHistory={openRevisionHistoryPane}
            />
          ) : (
            <IcfDocumentCardReadOnlyRenewal
              status={latestRevision.latestStatus}
              numberingId={icfDocument.numberingId}
              version={latestRevision.version}
              name={latestRevision.name}
              documentType={latestRevision.type}
              fileName={latestRevision.fileName}
              filePreviewUrl={latestFilePreviewUrl}
              isRequiredPatientSign={latestRevision.isRequiredPatientSign}
              signFields={latestRevision.signFields}
              onRedirectDocuSignPreview={onRedirectDocuSignPreview}
              hasMultipleRevisionHistories={hasMultipleRevisionHistories}
              hasTempFile={hasTempFile}
              openRevisionHistory={openRevisionHistoryPane}
            />
          )}
        </Box>
        <Box overflow="auto" mt="8">
          <Text fontWeight="bold">変更履歴</Text>
          <Table mt="2">
            <Thead>
              <Tr>
                <Th w="20%">操作</Th>
                <Th w="30%">操作日時</Th>
                <Th w="20%">操作者</Th>
                <Th w="30%">コメント</Th>
              </Tr>
            </Thead>
            <Tbody>
              {latestRevision.histories.sort(operatedAtDesc).map(history => (
                <Tr key={history.uid}>
                  <Td>{icfRevActionText[history.action]}</Td>
                  <Td>
                    {formatDate(history.operatedAt, 'YYYY/MM/DD (ddd) HH:mm')}
                  </Td>
                  <Td maxW="200px">
                    <MemberLabel
                      role={history.operatedMemberRole}
                      displayName={history.operatedMemberName}
                    />
                  </Td>
                  <Td>{history.comment}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Flex>
  )
}
